import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from './cn.js';
/**
 * A native checkbox with the shared focus ring, 16 px, `input` border and
 * `primary` when checked. Deliberately native rather than a Radix one: a real
 * `<input type="checkbox">` keeps form semantics and assistive-technology
 * behaviour for free. `indeterminate` is a DOM property, so it is applied
 * through the ref.
 */
const Checkbox = React.forwardRef(({ className, label, description, indeterminate = false, disabled, id, ...props }, ref) => {
    const reactId = React.useId();
    const inputId = id ?? reactId;
    const inner = React.useRef(null);
    React.useEffect(() => {
        if (inner.current)
            inner.current.indeterminate = indeterminate;
    }, [indeterminate]);
    const input = (_jsx("input", { ref: (node) => {
            inner.current = node;
            if (typeof ref === 'function')
                ref(node);
            else if (ref)
                ref.current = node;
        }, id: inputId, type: "checkbox", "data-slot": "checkbox", disabled: disabled, className: cn('size-4 shrink-0 cursor-pointer rounded-sm border border-input-strong accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed', label == null && className), ...props }));
    if (label == null)
        return input;
    return (_jsxs("label", { htmlFor: inputId, className: cn('flex items-start gap-2 text-ui', disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer', className), children: [_jsx("span", { className: "flex h-5 items-center", children: input }), _jsxs("span", { className: "flex min-w-0 flex-col gap-0.5", children: [_jsx("span", { children: label }), description != null && _jsx("span", { className: "text-caption text-muted-foreground", children: description })] })] }));
});
Checkbox.displayName = 'Checkbox';
/** A set of checkboxes bound to one string array; the incoming option order is kept. */
export function CheckboxGroup({ options, value, onChange, disabled, orientation = 'horizontal', className, ...aria }) {
    return (_jsx("div", { role: "group", className: cn(orientation === 'vertical' ? 'flex flex-col gap-2' : 'flex flex-wrap gap-x-4 gap-y-2', className), ...aria, children: options.map((option) => (_jsx(Checkbox, { label: option.label, description: option.description, checked: value.includes(option.value), disabled: disabled || option.disabled, onChange: (event) => onChange(event.target.checked
                ? [...value, option.value]
                : value.filter((entry) => entry !== option.value)) }, option.value))) }));
}
export { Checkbox };
//# sourceMappingURL=checkbox.js.map